// Search tab inside Case Detail (SPEC §4.1.2 + §4.1.3).
//
// Three modes — hybrid (default), keyword, semantic — all scoped to the
// current Case via filters.case_id so the user searches "inside this
// matter" rather than the whole tenant corpus. Snippets returned by the
// keyword + hybrid surfaces carry <mark>...</mark> markers from
// ts_headline; we render them safely by splitting on the tags (no
// dangerouslySetInnerHTML).

import { FormEvent, Fragment, useState } from "react";
import { Link } from "react-router-dom";

import { ApiError, search as searchApi } from "../../lib/api";
import { ChunkHit, DocumentHit } from "../../lib/types";

type Mode = "hybrid" | "keyword" | "semantic";

interface Props {
  caseId: number;
}

interface DocumentResults {
  kind: "document";
  query: string;
  hits: DocumentHit[];
  total?: number;
}

interface ChunkResults {
  kind: "chunk";
  query: string;
  hits: ChunkHit[];
}

type Results = DocumentResults | ChunkResults;

export function CaseSearchTab({ caseId }: Props): JSX.Element {
  const [mode, setMode] = useState<Mode>("hybrid");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Results | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runSearch = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(null);
    try {
      const filters = { case_id: caseId };
      if (mode === "semantic") {
        const r = await searchApi.semantic(q, filters);
        setResults({ kind: "chunk", query: r.query, hits: r.hits });
      } else if (mode === "keyword") {
        const r = await searchApi.keyword(q, filters);
        setResults({
          kind: "document",
          query: r.query,
          hits: r.hits,
          total: r.total_matched,
        });
      } else {
        const r = await searchApi.hybrid(q, filters);
        setResults({ kind: "document", query: r.query, hits: r.hits });
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Search failed");
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="card p-5">
      <div className="mb-3">
        <h2 className="text-lg font-medium text-slate-800">Search in this Case</h2>
        <p className="mt-1 text-xs text-slate-500">
          Hybrid combines keyword (Postgres tsvector) and semantic
          (pgvector cosine over nomic-embed-text chunks). Keyword is best
          for exact phrases; semantic finds passages by meaning even when
          no words match.
        </p>
      </div>

      <form onSubmit={runSearch} className="mb-4 flex flex-wrap items-stretch gap-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search this Case…"
          aria-label="Search query"
          className="input min-w-0 flex-1"
        />
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as Mode)}
          aria-label="Search mode"
          className="input w-36 flex-none"
        >
          <option value="hybrid">Hybrid</option>
          <option value="keyword">Keyword</option>
          <option value="semantic">Semantic</option>
        </select>
        <button type="submit" className="btn-primary flex-none" disabled={loading || !query.trim()}>
          {loading ? "Searching…" : "Search"}
        </button>
      </form>

      {error && (
        <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
      )}

      {!results && !loading && !error && (
        <p className="text-sm text-slate-500">
          Enter a query above. Results are scoped to documents associated with this Case.
        </p>
      )}

      {results && results.hits.length === 0 && (
        <p className="text-sm text-slate-600">
          No matches for <span className="font-medium">"{results.query}"</span>.
        </p>
      )}

      {results && results.kind === "document" && results.hits.length > 0 && (
        <div>
          <p className="mb-2 text-xs text-slate-500">
            {results.hits.length} document{results.hits.length === 1 ? "" : "s"}
            {results.total !== undefined && results.total !== results.hits.length
              ? ` (of ${results.total} matched)`
              : ""}{" "}
            for <span className="font-medium">"{results.query}"</span>
          </p>
          <ul role="list" className="divide-y divide-slate-200">
            {results.hits.map((h) => (
              <DocumentHitRow key={h.document_id} hit={h} />
            ))}
          </ul>
        </div>
      )}

      {results && results.kind === "chunk" && results.hits.length > 0 && (
        <div>
          <p className="mb-2 text-xs text-slate-500">
            {results.hits.length} chunk{results.hits.length === 1 ? "" : "s"} for{" "}
            <span className="font-medium">"{results.query}"</span> — ranked by semantic similarity
          </p>
          <ul role="list" className="divide-y divide-slate-200">
            {results.hits.map((h) => (
              <ChunkHitRow key={`${h.document_id}:${h.chunk_index}`} hit={h} />
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function DocumentHitRow({ hit }: { hit: DocumentHit }): JSX.Element {
  return (
    <li className="py-3">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/documents/${hit.document_id}`}
          className="truncate font-medium text-slate-900 hover:text-brand-700"
        >
          {hit.filename}
        </Link>
        <span className="flex-none text-xs text-slate-400">
          score {hit.score.toFixed(4)}
        </span>
      </div>
      <div className="text-xs text-slate-500">
        {hit.doc_type ?? "uncategorized"}
        {hit.cross_checkable && <span className="ml-2">· cross-checkable</span>}
      </div>
      {hit.snippet && (
        <p className="mt-1 text-sm leading-relaxed text-slate-700">
          <MarkedSnippet text={hit.snippet} />
        </p>
      )}
    </li>
  );
}

function ChunkHitRow({ hit }: { hit: ChunkHit }): JSX.Element {
  return (
    <li className="py-3">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/documents/${hit.document_id}`}
          className="truncate font-medium text-slate-900 hover:text-brand-700"
        >
          {hit.filename}
        </Link>
        <span className="flex-none text-xs text-slate-400">
          chunk #{hit.chunk_index} · score {hit.score.toFixed(3)}
        </span>
      </div>
      <div className="text-xs text-slate-500">{hit.doc_type ?? "uncategorized"}</div>
      <p className="mt-1 text-sm leading-relaxed text-slate-700 line-clamp-4">{hit.text}</p>
    </li>
  );
}

// Render a snippet that contains <mark>...</mark> markers from ts_headline.
// We never use dangerouslySetInnerHTML — the snippet is split on the
// markers and the marked segments are rendered as real <mark> JSX
// elements. Everything between markers is plain text.
function MarkedSnippet({ text }: { text: string }): JSX.Element {
  const parts = text.split(/(<mark>|<\/mark>)/);
  let inMark = false;
  return (
    <>
      {parts.map((part, i) => {
        if (part === "<mark>") {
          inMark = true;
          return null;
        }
        if (part === "</mark>") {
          inMark = false;
          return null;
        }
        if (inMark) {
          return (
            <mark key={i} className="rounded bg-yellow-100 px-0.5">
              {part}
            </mark>
          );
        }
        return <Fragment key={i}>{part}</Fragment>;
      })}
    </>
  );
}
